<template>
  <div
    ref="containerRef"
    :class="[
      'customize-form-wrap',
      submiting && 'customize-form-submiting',
    ]"
  >
    <div v-if="initFail" class="customize-form-initfail">
      {{ initFail }}
      <cust-button
        size="small"
        @click="onReload"
      >
        {{ i18n('refresh') }}
      </cust-button>
    </div>
    <div v-if="loading" class="customize-form-loading">
      <skeleton animated />
    </div>

    <template v-else>
      <template v-if="formVisible">
        <basic-form
          ref="formRef"
          :schema="schema"
          :locale="locale"
          :is-mobile="isMobile"
          :hide-index="hideIndex"
          :district-data="districtData"
          :city-data="cityData"
          :province-data="provinceData"
        />

        <div v-if="!staticOnly" v-show="nvcVisible" ref="nvcRef" :id="awscKey"></div>

        <cust-button
          v-if="!staticOnly"
          v-show="!nvcVisible"
          class="customize-form-submit"
          v-coverage="handleSubmitShow"
          :loading="submiting"
          @click="onSubmit"
        >
          {{ i18n('submit') }}
        </cust-button>
        <cust-button
          v-if="fixedVisible"
          class="customize-form-submit customize-form-submit-fixed"
          :loading="submiting"
          @click="onSubmit"
        >
          {{ i18n('submit') }}
        </cust-button>
      </template>
    </template>
  </div>
</template>

<script>
import Vue from 'vue';
import BasicForm from './basic';
import CustButton from './component/button';
import loadingIcon from './component/icon/loading';
import Skeleton from './component/skeleton';
import {
  i18n,
  locales,
  fontWeight,
  ERR_CODE_SERVER,
  ERR_CODE_INITFAIL,
  FIELD_TYPE_CITYSELECT,
  CITYSELECT_LEVEL_CITY,
  CITYSELECT_LEVEL_PROVINCE,
} from './constant';
import styleMixin from './mixin/style';
import eventMixin from './mixin/event';
import coverageDirective from './directive/coverage';
import { isFunction, getJSONString, getRandomUid, isObject } from './utils';
import {
  get,
  post,
  getTokenKey,
  SERVER_ENV_PROD,
  REQ_URL_CN_TEST,
  REQ_URL_CN_PROD,
  REQ_URL_GLOBAL_PROD,
} from './utils/request';
import { TrancelessAwsc } from './utils/awsc';
import './style/index.scss';

if (Vue) {
  Vue.directive(coverageDirective.name, coverageDirective);
}

const RETRY_TIME = 3;

export default {
  name: 'CustomizeForm',
  components: {
    BasicForm,
    CustButton,
    loadingIcon,
    Skeleton,
  },
  props: {
    __formSchema__: {   // 用于描述属性表单结构的内置属性
      type: Object,
      default: () => {
        return {
          url: {
            type: 'Select',
            label: '表单',
            required: true,
            componentAttrs: {
              dataSource: {
                params: {},
                remoteType: 'query',
                remoteMethod: 'FormGroups',
              },
            },
            index: 1,
          },
          staticOnly: {
            type: 'Switch',
            label: '仅使用静态组件',
            index: 2,
          },
          locale: {
            type: 'Select',
            label: '语种 (内置文案)',
            required: true,
            componentAttrs: {
              options: locales,
            },
            index: 2,
          },
          asyncLoad: {
            type: 'Switch',
            label: '异步手动加载表单',
            index: 3,
          },
          closeSubmitFixed: {
            type: 'Switch',
            label: '是否关闭提交按钮悬浮底部的功能',
            index: 4,
            isHidden: [
              {
                field: 'staticOnly',
                regex: '^true$'
              }
            ],
          },
          hideIndex: {
            type: 'Switch',
            label: '是否隐藏表单项索引',
            index: 4,
          },
          mobileSize: {
            type: 'Number',
            label: '切换为移动端样式的尺寸 (单位: px)',
            required: true,
            index: 5,
          },
          borderRaduis: {
            type: 'Number',
            label: '提交按钮等边框的圆角 (单位: px)',
            required: true,
            index: 6,
          },
          primaryColor: {
            type: 'ColorPicker',
            label: '【主题色】表单项选中等颜色',
            required: true,
            index: 7,
          },
          dangerColor: {
            type: 'ColorPicker',
            label: '【危险色】错误提示等颜色',
            required: true,
            index: 8,
          },
          borderColor: {
            type: 'ColorPicker',
            label: '【表单项边框】颜色',
            required: true,
            index: 9,
          },
          bgColor: {
            type: 'ColorPicker',
            label: '【表单项背景】颜色',
            required: true,
            index: 9,
          },
          placeholderColor: {
            type: 'ColorPicker',
            label: '【表单项占位文字】颜色',
            required: true,
            index: 9,
          },
          titleFontSize: {
            type: 'FontSize',
            label: '【表单项标题】字号',
            required: true,
            index: 10,
          },
          titleFontWeight: {
            type: 'Select',
            label: '【表单项标题】字重',
            required: true,
            index: 11,
            componentAttrs: {
              options: fontWeight,
            },
          },
          titleColor: {
            type: 'ColorPicker',
            label: '【表单项标题】颜色',
            required: true,
            index: 12,
          },
          textFontSize: {
            type: 'FontSize',
            label: '【表单项内容】字号',
            required: true,
            index: 13,
          },
          textFontWeight: {
            type: 'Select',
            label: '【表单项内容】字重',
            required: true,
            index: 14,
            componentAttrs: {
              options: fontWeight,
            },
          },
          textColor: {
            type: 'ColorPicker',
            label: '【表单项内容】颜色',
            required: true,
            index: 15,
          },
          multiTagFontSize: {
            type: 'FontSize',
            label: '【多选题提示标签】字号',
            required: true,
            index: 16,
          },
          multiTagFontWeight: {
            type: 'Select',
            label: '【多选题提示标签】字重',
            required: true,
            index: 17,
            componentAttrs: {
              options: fontWeight,
            },
          },
          multiTagColor: {
            type: 'ColorPicker',
            label: '【多选题提示标签】颜色',
            required: true,
            index: 18,
          },
          descFontSize: {
            type: 'FontSize',
            label: '【描述】字号',
            required: true,
            index: 16,
          },
          descFontWeight: {
            type: 'Select',
            label: '【描述】字重',
            required: true,
            index: 17,
            componentAttrs: {
              options: fontWeight,
            },
          },
          descColor: {
            type: 'ColorPicker',
            label: '【描述】颜色',
            required: true,
            index: 18,
          },
          submitFontSize: {
            type: 'FontSize',
            label: '【提交按钮】字号',
            required: true,
            index: 19,
            isHidden: [
              {
                field: 'staticOnly',
                regex: '^true$'
              }
            ],
          },
          submitFontWeight: {
            type: 'Select',
            label: '【提交按钮】字重',
            required: true,
            index: 20,
            componentAttrs: {
              options: fontWeight,
            },
            isHidden: [
              {
                field: 'staticOnly',
                regex: '^true$'
              }
            ],
          },
          submitColor: {
            type: 'ColorPicker',
            label: '【提交按钮】字体颜色',
            required: true,
            index: 21,
            isHidden: [
              {
                field: 'staticOnly',
                regex: '^true$'
              }
            ],
          },
          submitBg: {
            type: 'Image',
            label: '【提交按钮】背景图片(不填时使用主题色填充)',
            index: 22,
            isHidden: [
              {
                field: 'staticOnly',
                regex: '^true$'
              }
            ],
          },
        };
      },
    },

    staticOnly: {       // 仅使用静态组件，关闭所有与服务端交互功能
      type: Boolean,
      default: false,
    },
    asyncLoad: {        // 异步手动加载表单
      type: Boolean,
      default: false,
    },
    closeSubmitFixed: { // 是否关闭提交按钮固定在页面底部的功能
      type: Boolean,
      default: false,
    },
    hideIndex: {        // 是否隐藏表单项索引
      type: Boolean,
      default: false,
    },
    mobileSize: {       // 定义什么尺寸下展示移动端样式
      type: Number,
      default: 750,
    },
    url: {              // 表单地址
      type: String,
      // default: 'https://website.xdcdn.net/form/customize_form/internal.taptap/NDSPqhQhnNmNbmch.json',
      default: 'https://website.xdcdn.net/form/customize_form/internal/NCvpceFILADXnxJj.json',
    },
    locale: {           // 当前表单语种
      type: String,
      default: 'zh_CN',
    },
    primaryColor: {     // 主题色，表单项选中时的颜色
      type: String,
      default: '#409EFF',
    },
    dangerColor: {      // 必填标签及错误时颜色
      type: String,
      default: '#F56C6C',
    },
    borderColor: {      // 表单项边框颜色
      type: String,
      default: '#DCDFE6',
    },
    borderRaduis: {     // 所有圆角，提交按钮、错误提示、input框等
      type: Number,
      default: 4,
    },
    titleColor: {       // 表单项标题颜色
      type: String,
      default: '#606266',
    },
    titleFontSize: {    // 表单项标题字号
      type: String,
      default: '14px',
    },
    titleFontWeight: {  // 表单项标题字重
      type: Number,
      default: 500,
    },
    textColor: {        // 表单项内容颜色
      type: String,
      default: '#606266',
    },
    textFontSize: {     // 表单项内容字号
      type: String,
      default: '14px',
    },
    textFontWeight: {   // 表单项内容字重
      type: Number,
      default: 500,
    },
    bgColor: {          // 表单项背景颜色
      type: String,
      default: '#ffffff',
    },
    placeholderColor: { // 表单项占位文字颜色
      type: String,
      default: '#c0c4cc',
    },
    multiTagColor: {    // 多选题提示标签颜色
      type: String,
      default: '#b7b7b7',
    },
    multiTagFontSize: { // 多选题提示标签字号
      type: String,
      default: '14px',
    },
    multiTagFontWeight: {// 多选题提示标签字重
      type: Number,
      default: 500,
    },
    descColor: {    // 描述标签颜色
      type: String,
      default: '#b7b7b7',
    },
    descFontSize: { // 描述标签字号
      type: String,
      default: '14px',
    },
    descFontWeight: {// 描述标签字重
      type: Number,
      default: 500,
    },
    submitBg: String,    // 提交按钮背景图片，不存在时使用主题色填充
    submitColor: {       // 提交按钮字体颜色
      type: String,
      default: '#ffffff',
    },
    submitFontSize: {    // 提交按钮字号
      type: String,
      default: '14px',
    },
    submitFontWeight: {  // 提交按钮字重
      type: Number,
      default: 500,
    },
  },
  mixins: [styleMixin, eventMixin],

  data() {
    this.eventTimer = null;
    this.awscInstance = null;
    this.reqBaseUrl = '';
    this.districtRetry = 0;
    this.cityRetry = 0;
    this.provinceRetry = 0;

    return {
      loading: true,
      isMobile: false,
      endAlready: false,
      submitFixed: false,
      submiting: false,
      formVisible: false,
      nvcVisible: false,
      initFail: null,
      code: '',
      project: '',
      schema: [],
      awscKey: '',
      provinceData: [],
      cityData: [],
      districtData: [],
    };
  },

  mounted() {
    this.listenDevice();
    if (this.asyncLoad === true) {
      this.loading = false;
    } else {
      this.initForm();
    }
  },

  beforeDestroy() {
    this.clearEventTimer();
    this.removeListener();
    this.destroyAwsc();
  },

  computed: {
    fixedVisible() {
      return this.submitFixed && !this.nvcVisible && !this.staticOnly;
    },
  },

  watch: {
    url: {
      immediate: true,
      handler(val) {
        if (val) {
          const matches = this.url.match(/customize_form\/(.*)\/(.*).json/) || [];
          this.code = matches[2];
          this.project = matches[1];
        }
      },
    },
  },

  methods: {
    requestHandler(url, params, method = 'get') {
      switch (method) {
        case 'post':
          return post(this.reqBaseUrl + url, params, this.code);
        default:
          return get(this.reqBaseUrl + url, params, this.code);
      }
    },

    i18n(key) {
      return i18n(this.locale, key);
    },

    initForm() {
      
      this.loading = true;

      // 初始化阿里云校验
      if (!this.staticOnly) {
        this.initAwsc();
      }

      this.getFormInfo()
        .then((res) => {
          const { schema = [], env, is_intl } = res;
          this.setReqBaseUrl(env, is_intl);

          if (!this.staticOnly) {
            // 获取填写记录
            this.getFormRecord(this.code)
              .then((res) => {
                this.emitHasSubmittedStatus(res && res.is_submit);
              })
              .catch(() => {
                this.emitHasSubmittedStatus(false);
              })
              .finally(() => {
                this.loading = false;
              });
          } else {
            this.loading = false;
          }

          this.getAddressOptions(schema);          

          this.schema = schema;
          this.formVisible = true;
          this.initFail = null;
          this.emitAfterInit(true);
        })
        .catch(() => {
          this.initFail = this.i18n(ERR_CODE_INITFAIL);
          this.emitAfterInit(false);
          this.loading = false;
        });
    },

    initAwsc() {
      this.destroyAwsc();

      this.awscKey = getRandomUid();
      this.awscInstance = new TrancelessAwsc({
        element: this.awscKey,
        scene: 'nvc_activity_h5',
        appkey: 'FFFF0N2N00000000B257',
        language: this.locale,
        onSuccess: (nvcData) => {
          const values = this.$refs.formRef.getValues();
          this.nvcVisible = false;
          this.doSubmit(values, nvcData);
        },
        onFail: (failCode) => {
          console.log('awsc fail', failCode);
        },
        onError: (errorCode) => {
          console.log('awsc error', errorCode);
        },
      });

      this.awscInstance.init()
        .then(() => {})
        .catch(() => {
          this.$nextTick(() => {
            this.awscInstance.init();
          });
        });
    },

    destroyAwsc() {
      this.awscInstance && this.awscInstance.destroy();
    },

    setReqBaseUrl(env, isInternal) {
      if (isInternal) {
        this.reqBaseUrl = REQ_URL_GLOBAL_PROD;
      } else {
        this.reqBaseUrl = env === SERVER_ENV_PROD ? REQ_URL_CN_PROD : REQ_URL_CN_TEST;
      }
    },

    getFormRecord(code) {
      return this.requestHandler(`/result/${code}`);
    },

    getDevice(immediate) {
      if (immediate) {
        this.isMobile = window.innerWidth <= this.mobileSize;
        return;
      }

      this.clearEventTimer();
      this.eventTimer = setTimeout(() => {
        this.isMobile = window.innerWidth <= this.mobileSize;
      }, 500);
    },

    clearEventTimer() {
      this.eventTimer && clearTimeout(this.eventTimer);
    },

    listenDevice() {
      this.getDevice(true);
      window.addEventListener('resize', this.getDevice);
    },

    removeListener() {
      window.removeEventListener('resize', this.getDevice);
    },

    getFormInfo() {
      return fetch(this.url)
        .then((res) => {
          return res.json();
        });
    },

    onSubmit() {
      this.validateForm((values, isInvalidResult) => {
        this.handleSubmit(values, isInvalidResult);
      });
    },

    handleSubmitShow(entry) {
      if (this.closeSubmitFixed) return;

      if (this.endAlready) {
        if (entry.isIntersecting) {
          this.submitFixed = false;
        } else if (entry.boundingClientRect && entry.boundingClientRect.y > 0) {
          this.submitFixed = true;
        }
        return;
      }

      if (entry.isIntersecting) {
        this.endAlready = true;
      }
    },

    validateForm(successCb, errorCb) {
      const formRef = this.$refs.formRef;
      if (formRef) {
        formRef.validate((success, messages) => {
          const values = formRef.getValues();

          if (!success) {
            this.scrollToFstErr(formRef);
            this.emitValidateFail(values, messages || {});
            errorCb && errorCb(values, messages || {});
            return;
          }

          this.emitValidateSuccess(values);
          const isInvalidResult = formRef.isInvalidResult();
          successCb && successCb(values, isInvalidResult);
        });
      }
    },

    scrollToFstErr(formRef) {
      try {
        const formItems = formRef.$children[0].$children;
        for (const formItem of formItems) {
          if (formItem.validateState === 'error') {
            formItem.$el.scrollIntoView({ behavior: 'smooth' });
            return;
          }
        }
      } catch {}
    },

    resetForm() {
      const formRef = this.$refs.formRef;
      if (isFunction(formRef && formRef.reset)) {
        formRef.reset();
      }
    },

    getResponseError(err) {
      const errCode = err.error || ERR_CODE_SERVER;
      return {
        code: errCode,
        message: this.i18n(errCode) || this.i18n(ERR_CODE_SERVER),
      };
    },

    handleSubmit(values, isInvalidResult) {
      this.submiting = true;
      this.awscInstance.reset();
      this.awscInstance.getAwscResult((nvcData) => {
        this.doSubmit(values, nvcData, isInvalidResult);
      });
    },

    doSubmit(values, nvcData, isInvalidResult) {
      const code = this.code;
      const project = this.project;
      const page_code = window.moo && window.moo.code;
      if (project && code) {
        this.requestHandler(
          '/create',
          {
            code,
            project,
            page_code: page_code,
            content: getJSONString(values),
            nvc_data: nvcData,
            is_invalid: isInvalidResult,
          },
          'post'
        )
          .then((res = {}) => {
            if (res.is_check) {
              this.renderAwsc();
              return;
            }

            const token = res.token || '';
            localStorage.setItem(getTokenKey(code), token);
            this.emitSaveSuccess(
              {
                ...values
              },
              {
                code,
                page_code,
                token,
                project,
              }
            );
          })
          .catch((err) => {
            const error = this.getResponseError(err);
            this.emitSaveFail(error);
          })
          .finally(() => {
            this.submiting = false;
          });
      }
    },

    renderAwsc() {
      this.nvcVisible = true;
      this.awscInstance.renderValidator();
      this.$nextTick(() => {
        this.$refs.nvcRef.scrollIntoView({ behavior: 'smooth' });
      });
    },

    onReload() {
      this.initFail = null;
      this.initForm();
    },

    getAddressOptions(schema) {
      let cityInited = false;
      let districtInited = false;
      let provinceInited = false;

      schema.forEach((item) => {
        if (item.type === FIELD_TYPE_CITYSELECT) {
          const level = item.componentAttrs && item.componentAttrs.level;
          switch(level) {
            case CITYSELECT_LEVEL_PROVINCE:
              if (!provinceInited) {
                provinceInited = true;
                this.getProvinceData();
              }
              break;

            case CITYSELECT_LEVEL_CITY:
              if (!cityInited) {
                cityInited = true;
                this.getCityData();
              }
              break;

            default:
              if (!districtInited) {
                districtInited = true;
                this.getDistrictData();
              }
          }
        }
      });
    },

    handleGetAddressData(url, successCb, errorCb) {
      fetch(url)
        .then((res) => res.json())
        .then((data) => {
          successCb(data || []);
        })
        .catch(() => {
          errorCb();
        });
    },

    getProvinceData() {
      this.handleGetAddressData(
        'https://website.xdcdn.net/form/customize_form/resource/provinces.json',
        (data) => {
          this.provinceData = data;
        },
        () => {
          if (this.provinceRetry < RETRY_TIME) {
            setTimeout(() => {
              this.getProvinceData();
            }, 3000);
            this.provinceRetry++;
          }
        }
      );
    },

    getCityData() {
      this.handleGetAddressData(
        'https://website.xdcdn.net/form/customize_form/resource/cities.json',
        (data) => {
          this.cityData = data;
        },
        () => {
          if (this.cityRetry < RETRY_TIME) {
            setTimeout(() => {
              this.getCityData();
            }, 3000);
            this.cityRetry++;
          }
        }
      );
    },

    getDistrictData() {
      this.handleGetAddressData(
        'https://website.xdcdn.net/form/customize_form/resource/districts.json',
        (data) => {
          this.districtData = data;
        },
        () => {
          if (this.districtRetry < RETRY_TIME) {
            setTimeout(() => {
              this.getDistrictData();
            }, 3000);
            this.districtRetry++;
          }
        }
      );
    },

    setValues(values) {
      if (isObject(values)) {
        this.$refs.formRef.setValues(values);
      }
    },
  },
};
</script>